import { useEffect, useRef } from 'react';
import { effectCalculator } from '../services/effectCalculator';
import { systemBus, EVENTS } from '../services/systemBus';
// [Phase B / #047 — MAY 2026]: spellcaster-level helper. Pre-fix the
//   logging path here had a hardcoded
//   `subclass === 'eldritch knight' || subclass === 'arcane trickster'`
//   fallback for third-caster subclasses. Replaced by
//   computeSpellcasterLevel which goes through getCasterType (which
//   already handles subclasses case-insensitively).
import { computeSpellcasterLevel } from '../utils/spellcasterLevel';

/**
 * [PURPOSE]: Automatically recalculate spell slots and class resources when classes change
 * [WHY]: Multiclass characters need accurate spell slot calculations
 * [INTEGRATION]: Used in App.jsx to watch playerStats.classes array
 * [PHASE 4D FIX]: Now uses effectCalculator for centralized multiclass spell slot calculation
 *
 * @param {Object} character - Full character object with classes array
 * @param {Function} setSpellSlots - State setter for spell slots
 * @param {Function} DEPRECATED_calculateMulticlassSpellcasterLevel - DEPRECATED - kept for backward compatibility
 * @param {Function} logActivity - Activity logging function
 */
export const useClassEffects = (
  character,
  setSpellSlots,
  DEPRECATED_calculateMulticlassSpellcasterLevel, // Keep param for backward compatibility
  logActivity
) => {
  // Track previous classes to detect changes
  const previousClassesRef = useRef(null);

  useEffect(() => {
    const currentClasses = character?.classes || [];

    // Skip if no classes (new character)
    if (currentClasses.length === 0) {
      previousClassesRef.current = currentClasses;
      return;
    }

    // Detect if classes changed (added, removed, or level changed)
    const previousClasses = previousClassesRef.current;
    if (previousClasses) {
      const hasChanged =
        previousClasses.length !== currentClasses.length ||
        previousClasses.some((prevClass, index) => {
          const currentClass = currentClasses[index];
          return !currentClass ||
            prevClass.className !== currentClass.className ||
            prevClass.level !== currentClass.level;
        });

      if (!hasChanged) {
        previousClassesRef.current = currentClasses;
        return;
      }
    }

    // [FEATURE]: Auto-recalculate spell slots for spellcasting classes
    // [PHASE 4D FIX]: Use centralized effectCalculator instead of duplicate logic

    // Use effectCalculator's centralized multiclass spell slot calculation
    // This correctly implements D&D 5e PHB p.164-165 multiclass spellcaster rules
    const newSpellSlots = effectCalculator.calculateMulticlassSpellSlots(character);

    // Check if character has any spell slots
    const hasSpellSlots = Object.values(newSpellSlots).some(slot => slot.max > 0);

    if (hasSpellSlots) {
      // Preserve existing 'used' counts where possible
      setSpellSlots(prevSlots => {
        const updatedSlots = {};
        for (let level = 1; level <= 9; level++) {
          updatedSlots[level] = {
            max: newSpellSlots[level].max,
            // Keep existing used count if it's valid, otherwise reset to 0
            used: Math.min(
              prevSlots?.[level]?.used || 0,
              newSpellSlots[level].max
            )
          };
        }
        return updatedSlots;
      });

      if (logActivity) {
        // [Phase B / #047 — MAY 2026]: data-driven multiclass spellcaster
        //   level. computeSpellcasterLevel walks classes[] and falls
        //   through to getCasterType(subclass) for third-caster
        //   subclasses, so any custom CW subclass authoring
        //   `casterType: 'third'` participates without code edits.
        const spellcasterLevel = computeSpellcasterLevel(currentClasses);

        const totalSlots = Object.values(newSpellSlots).reduce((sum, slot) => sum + slot.max, 0);
        logActivity(
          `Spell slots recalculated (Spellcaster level ${spellcasterLevel})`,
          'character',
          {
            spellcasterLevel,
            totalSlots,
            breakdown: Object.entries(newSpellSlots)
              .filter(([_, slot]) => slot.max > 0)
              .map(([level, slot]) => `Level ${level}: ${slot.max} slots`),
            classes: currentClasses.map(c => `${c.className} ${c.level}`)
          }
        );
      }
    }

    // [ARC-012 FIX]: Emit event so other systems know class changed
    systemBus.emit(EVENTS.CLASS_CHANGED, {
      oldClasses: previousClassesRef.current,
      newClasses: currentClasses,
      source: 'useClassEffects'
    });

    // Update ref for next comparison
    previousClassesRef.current = currentClasses;

  }, [character, setSpellSlots, DEPRECATED_calculateMulticlassSpellcasterLevel, logActivity]);
};

export default useClassEffects;
